import { useEffect, useRef, useMemo } from 'react';
import './GrowerMap.css';

/**
 * Renders a Leaflet map with circle markers for each grower.
 *
 * @param {Array} growers - Array of grower objects from the API
 *   { farm_name, region, latitude, longitude, certifications, ... }
 */
const GrowerMap = ({ growers = [] }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  // Normalize to a consistent shape for the map, memoized for stable dependency
  const markers = useMemo(
    () =>
      growers
        .filter(g => g.latitude != null && g.longitude != null)
        .map(g => ({
          lat: parseFloat(g.latitude),
          lng: parseFloat(g.longitude),
          label: g.farm_name,
          region: g.region,
        })),
    [growers]
  );

  useEffect(() => {
    const L = window.L;
    if (!L || !containerRef.current || markers.length === 0) return;

    // Clean up previous map instance if it exists
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    markers.forEach(marker => {
      // Build popup content via DOM API to prevent XSS from user-supplied data
      const popupEl = document.createElement('div');
      const nameEl = document.createElement('strong');
      nameEl.textContent = marker.label;
      popupEl.appendChild(nameEl);

      if (marker.region) {
        popupEl.appendChild(document.createElement('br'));
        const regionEl = document.createElement('span');
        regionEl.className = 'grower-map__popup-region';
        regionEl.textContent = marker.region;
        popupEl.appendChild(regionEl);
      }

      L.circleMarker([marker.lat, marker.lng], {
        radius: 9,
        fillColor: '#bc9038',
        fillOpacity: 0.9,
        color: '#433e32',
        weight: 2,
      })
        .bindPopup(popupEl)
        .addTo(map);
    });

    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 7);
    } else {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [markers]);

  if (markers.length === 0) {
    // Show fallback if Leaflet failed to load but we have grower data
    if (growers.some(g => g.latitude != null && g.longitude != null) && !window.L) {
      return (
        <div className="grower-map grower-map--fallback">
          <p className="grower-map__fallback-text">Map unavailable</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="grower-map">
      <div
        ref={containerRef}
        className="grower-map__map"
      />
    </div>
  );
};

export default GrowerMap;
